// chill-sound: chillandout soundscape orchestrator + first-visit prompt pill.
//
// Per §8 of the brief — four signature sounds, OFF by default, opt-in via a
// soft prompt on first visit. All triggers go through a central handler that
// silently no-ops when sound is off.
//
// Spec: docs/MASTER-BRIEF.md §8
// Budget: ~2 KB gzip. No deps.

const STORAGE_ON = 'chillandout:sound';            // 'on' | 'off' | unset
const STORAGE_PROMPT_SEEN = 'chillandout:sound-prompt-seen';
const VOLUME = 0.13; // -18 dB master per §8.2 (≈ 12.6%)

// §8.1 — four signature sounds, no more.
const SOUND_NAMES = ['chime', 'tap', 'rise', 'whoosh'];

class ChillSound extends HTMLElement {
  constructor() {
    super();
    this._cache = {};
    this._preloaded = false;
    this._destroyed = false;
    this._onPlay = this._onPlay.bind(this);
    this._onFirstInteraction = this._onFirstInteraction.bind(this);
  }

  connectedCallback() {
    if (document.querySelector('chill-sound:not(:scope)')) return; // singleton

    document.addEventListener('chillandout:play-sound', this._onPlay);
    // Trigger wiring per §8.1.
    document.addEventListener('chillandout:cart-added', () => this._play('tap'));
    document.addEventListener('chillandout:wishlist:changed', (e) => {
      // Chime on save only — not on remove (per §8.1: "Wishlist add").
      if (e && e.detail && e.detail.saved === true) this._play('chime');
    });

    // Preload sounds on first user interaction (autoplay policy §8.2).
    ['pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
      document.addEventListener(ev, this._onFirstInteraction, { once: true, passive: true })
    );

    // Show the soft prompt on first visit if user hasn't dismissed it.
    this._maybeShowPrompt();
  }

  disconnectedCallback() {
    this._destroyed = true;
    document.removeEventListener('chillandout:play-sound', this._onPlay);
    // Best-effort: cancel any outstanding sources
    Object.values(this._cache).forEach((a) => { try { a.pause(); } catch {} });
    this._cache = {};
  }

  /* ---------- core play path ---------- */

  _isOn() {
    try { return localStorage.getItem(STORAGE_ON) === 'on'; } catch { return false; }
  }

  _onPlay(e) {
    const name = e && e.detail && e.detail.name;
    if (typeof name === 'string') this._play(name);
  }

  _play(name) {
    if (!this._isOn() || this._destroyed) return;
    if (!SOUND_NAMES.includes(name)) return;
    if (!this._preloaded) this._preload();
    const audio = this._cache[name];
    if (!audio) return;
    try {
      audio.currentTime = 0;
      audio.play().catch(() => { /* autoplay blocked — silent */ });
    } catch { /* AbortError on rapid retrigger — silent */ }
  }

  _onFirstInteraction() {
    if (this._isOn()) this._preload();
  }

  _preload() {
    if (this._preloaded) return;
    this._preloaded = true;
    for (const name of SOUND_NAMES) {
      const url = this.dataset[name + 'Url'] || this.dataset[name];
      if (!url) continue;
      const audio = new Audio();
      audio.preload = 'auto';
      audio.volume = VOLUME;
      // Defensive: a 404 fires `error`; we cache the element anyway so retries
      // don't keep allocating, and play() simply rejects silently.
      audio.src = url;
      this._cache[name] = audio;
    }
  }

  /* ---------- prompt pill UI ---------- */

  _maybeShowPrompt() {
    let seen = false;
    try { seen = localStorage.getItem(STORAGE_PROMPT_SEEN) === '1'; } catch {}
    if (seen) return;
    // Don't show prompt if reduced motion is on AND no preference yet —
    // the user's environment hints at minimal interruption.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Brief delay so the prompt doesn't compete with first paint.
    setTimeout(() => this._renderPrompt(), 1500);
  }

  _renderPrompt() {
    if (this._destroyed) return;
    if (document.querySelector('.chill-sound-prompt')) return;

    const wrap = document.createElement('div');
    wrap.className = 'chill-sound-prompt';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');

    const label = document.createElement('span');
    label.className = 'chill-sound-prompt__label';
    label.innerHTML = 'sound on?<span aria-hidden="true"> ✦</span>';
    wrap.appendChild(label);

    const yesBtn = document.createElement('button');
    yesBtn.type = 'button';
    yesBtn.className = 'chill-sound-prompt__btn chill-sound-prompt__btn--yes';
    yesBtn.textContent = 'yes';
    yesBtn.addEventListener('click', () => {
      this._setSound('on');
      this._dismissPrompt(wrap);
      // Play a soft chime to confirm the toggle.
      this._preload();
      this._play('chime');
    });

    const laterBtn = document.createElement('button');
    laterBtn.type = 'button';
    laterBtn.className = 'chill-sound-prompt__btn chill-sound-prompt__btn--later';
    laterBtn.textContent = 'later';
    laterBtn.setAttribute('aria-label', 'dismiss sound prompt');
    laterBtn.addEventListener('click', () => this._dismissPrompt(wrap));

    wrap.appendChild(yesBtn);
    wrap.appendChild(laterBtn);
    document.body.appendChild(wrap);
    requestAnimationFrame(() => wrap.classList.add('is-visible'));
  }

  _dismissPrompt(el) {
    try { localStorage.setItem(STORAGE_PROMPT_SEEN, '1'); } catch {}
    el.classList.remove('is-visible');
    setTimeout(() => { try { el.remove(); } catch {} }, 350);
  }

  _setSound(state) {
    try { localStorage.setItem(STORAGE_ON, state); } catch {}
  }
}

if (!customElements.get('chill-sound')) {
  customElements.define('chill-sound', ChillSound);
}
